import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, Users, Shield, FileText, Wrench } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const CurrentChallenges: React.FC<SalesQualificationStepProps> = ({ data, onUpdate }) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Current Challenges & Pain Points</h2>
        <p className="text-gray-600">
          Understanding your operational challenges helps us customize enterprise solutions for
          maximum impact
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Operational Challenges */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Operational Challenges
            </CardTitle>
            <CardDescription>Day-to-day management and operational pain points</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Inefficient maintenance request management',
                'Poor vendor coordination and oversight',
                'Lack of standardized processes and procedures',
                'Difficulty tracking assets and equipment',
                'Inconsistent service delivery across properties',
                'Manual, time-consuming administrative tasks',
                'Inadequate budget tracking and cost control',
                'Seasonal workload management challenges',
                'Lack of performance metrics and reporting',
                'Difficulty scaling operations as community grows',
              ].map((challenge) => (
                <div key={challenge} className="flex items-start space-x-2">
                  <Checkbox
                    id={`operational-${challenge}`}
                    checked={data.operationalChallenges?.includes(challenge) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('operationalChallenges', challenge, !!checked)
                    }
                  />
                  <Label htmlFor={`operational-${challenge}`} className="text-sm leading-relaxed">
                    {challenge}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Communication Issues */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Communication & Engagement
            </CardTitle>
            <CardDescription>
              Resident communication and community engagement challenges
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Low resident participation in community events',
                'Ineffective communication channels (email, newsletters)',
                'Difficulty reaching all residents consistently',
                'Lack of two-way communication and feedback',
                'Multiple communication platforms causing confusion',
                'Poor emergency communication capabilities',
                'Language barriers with diverse resident population',
                'Generational gaps in technology adoption',
                'Board/management transparency issues',
                'Resident complaints not being addressed efficiently',
              ].map((challenge) => (
                <div key={challenge} className="flex items-start space-x-2">
                  <Checkbox
                    id={`communication-${challenge}`}
                    checked={data.communicationChallenges?.includes(challenge) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('communicationChallenges', challenge, !!checked)
                    }
                  />
                  <Label htmlFor={`communication-${challenge}`} className="text-sm leading-relaxed">
                    {challenge}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Compliance & Legal */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Compliance & Legal Issues
            </CardTitle>
            <CardDescription>Regulatory compliance and legal challenges</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Keeping up with changing HOA/Condo regulations',
                'Document management and retention challenges',
                'Meeting disclosure and transparency requirements',
                'Fair Housing Act compliance concerns',
                'ADA accessibility compliance issues',
                'Insurance and liability management complexity',
                'Election and voting process challenges',
                'Architectural review and approval bottlenecks',
                'Covenant enforcement difficulties',
                'Legal document storage and accessibility',
              ].map((challenge) => (
                <div key={challenge} className="flex items-start space-x-2">
                  <Checkbox
                    id={`compliance-${challenge}`}
                    checked={data.complianceChallenges?.includes(challenge) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('complianceChallenges', challenge, !!checked)
                    }
                  />
                  <Label htmlFor={`compliance-${challenge}`} className="text-sm leading-relaxed">
                    {challenge}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security & Safety */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              Security & Safety Concerns
            </CardTitle>
            <CardDescription>Security, safety, and risk management challenges</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Inadequate access control and visitor management',
                'Outdated or insufficient security camera systems',
                'Emergency preparedness and response planning',
                'Vehicle and parking management issues',
                'Package theft and delivery security concerns',
                'Vandalism and property damage problems',
                'Safety hazards in common areas and amenities',
                'Lack of integrated security monitoring',
                'Inconsistent guest and contractor screening',
                'Cybersecurity concerns with digital systems',
              ].map((challenge) => (
                <div key={challenge} className="flex items-start space-x-2">
                  <Checkbox
                    id={`security-${challenge}`}
                    checked={data.securityChallenges?.includes(challenge) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('securityChallenges', challenge, !!checked)
                    }
                  />
                  <Label htmlFor={`security-${challenge}`} className="text-sm leading-relaxed">
                    {challenge}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Assessment */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Challenge Priority Assessment
          </CardTitle>
          <CardDescription>
            What is your biggest challenge that needs immediate attention?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={data.topPriority || ''}
            onValueChange={(value) => updateFormData('topPriority', value)}
          >
            <div className="space-y-3">
              {[
                { value: 'financial', label: 'Financial management and cost control' },
                { value: 'operational', label: 'Operational efficiency and automation' },
                { value: 'communication', label: 'Resident communication and engagement' },
                { value: 'compliance', label: 'Regulatory compliance and legal issues' },
                { value: 'security', label: 'Security and safety improvements' },
                { value: 'technology', label: 'Technology modernization and integration' },
                { value: 'growth', label: 'Scaling operations for community growth' },
                { value: 'governance', label: 'Board governance and transparency' },
                { value: 'maintenance', label: 'Asset management and maintenance' },
                { value: 'multiple', label: 'Multiple equally critical challenges' },
              ].map((priority) => (
                <div key={priority.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={priority.value} id={priority.value} />
                  <Label htmlFor={priority.value} className="text-sm">
                    {priority.label}
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Impact Assessment */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Challenge Impact Assessment</CardTitle>
          <CardDescription>
            How are these challenges currently affecting your community?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="challengeImpact">Describe the impact of your biggest challenges</Label>
            <Textarea
              id="challengeImpact"
              value={data.challengeImpact || ''}
              onChange={(e) => updateFormData('challengeImpact', e.target.value)}
              placeholder="e.g., Resident satisfaction declining, increased board workload, rising operational costs, compliance risks..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="previousSolutions">What solutions have you tried before?</Label>
            <Textarea
              id="previousSolutions"
              value={data.previousSolutions || ''}
              onChange={(e) => updateFormData('previousSolutions', e.target.value)}
              placeholder="e.g., Switched property management companies, implemented new software, hired consultants..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="solutionGaps">What gaps remain in your current solutions?</Label>
            <Textarea
              id="solutionGaps"
              value={data.solutionGaps || ''}
              onChange={(e) => updateFormData('solutionGaps', e.target.value)}
              placeholder="e.g., Lack integration between systems, still too manual, doesn't scale with growth..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Urgency and Budget Impact */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Implementation Urgency</CardTitle>
            <CardDescription>How urgent is solving these challenges?</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={data.urgencyLevel || ''}
              onValueChange={(value) => updateFormData('urgencyLevel', value)}
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="critical" id="critical" />
                  <Label htmlFor="critical" className="text-sm">
                    Critical - Need solution within 30-60 days
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="high" id="high" />
                  <Label htmlFor="high" className="text-sm">
                    High - Need solution within 3-6 months
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="medium" />
                  <Label htmlFor="medium" className="text-sm">
                    Medium - Would like solution within 6-12 months
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="low" />
                  <Label htmlFor="low" className="text-sm">
                    Low - Exploring options for future implementation
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Budget Impact</CardTitle>
            <CardDescription>How are current challenges affecting costs?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Increasing operational costs due to inefficiencies',
                'Rising insurance premiums due to risks',
                'Lost revenue from amenity underutilization',
                'Fines or penalties from compliance issues',
                'Expensive emergency repairs from poor maintenance',
                'Professional services costs (legal, consulting)',
                'Technology costs from multiple disconnected systems',
                'Staff overtime and turnover costs',
              ].map((impact) => (
                <div key={impact} className="flex items-start space-x-2">
                  <Checkbox
                    id={`budget-impact-${impact}`}
                    checked={data.budgetImpacts?.includes(impact) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('budgetImpacts', impact, !!checked)
                    }
                  />
                  <Label htmlFor={`budget-impact-${impact}`} className="text-sm leading-relaxed">
                    {impact}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Why challenge assessment matters for Enterprise solutions:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Enables custom solution design addressing your specific pain points</li>
          <li>• Helps prioritize implementation phases for maximum early impact</li>
          <li>• Calculates accurate ROI based on current cost of challenges</li>
          <li>• Identifies integration opportunities across multiple problem areas</li>
          <li>• Designs change management strategy for successful adoption</li>
        </ul>
      </div>
    </div>
  );
};
